import React, { useEffect, useMemo, useState } from 'react'
import {
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Controls,
  Edge,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

import { Answer, Question } from '../../../Types'
import { applyDagreLayout, buildEdges } from './graphUtils'
import { QuestionNode, QuestionNodeData } from './QuestionNode'
import { useProjectById } from '../../../hooks/useProjectById'
import { AssignmentDataStartEnd, useAssignments } from '../../../hooks/useAssignment'
import { getPrioritisedLanguage } from '../../../util/languages'
import { useParams } from 'react-router-dom'
import { isBranching } from './dependencyUtils'
import { groupBy, orderBy } from 'lodash'
import { COLOR_PRIMARY } from '../../../constants'
import { logAssignmentDebug } from '../logAssignmentDebug'

const nodeTypes = { questionNode: QuestionNode }

type ProjectedNode = {
  question: Question
  answers: Answer[]
  isWalked: boolean
}

export const DependencyAssignmentView = ({ subjectLanguageIsoCode }: { subjectLanguageIsoCode: string }) => {
  const { projectId, subjectId, assignmentId } = useParams() as {
    projectId: string
    subjectId: string
    assignmentId: string
  }

  const { data: projectData } = useProjectById(projectId)
  const { data }: { data: AssignmentDataStartEnd } = useAssignments(projectId, assignmentId, subjectId)

  const questions = data.assignment.questions
  const answers = data.assignment.answers

  const startId = data.startId
  const endId = data.endId

  // If one of the prioritised languages is present in the project, use that
  // — it will be checked on question level if a translation exists.
  const prioritisedLanguageIsoCode = getPrioritisedLanguage(projectData.languages)
  const lang = (prioritisedLanguageIsoCode ?? subjectLanguageIsoCode).toLowerCase()

  //
  // This is a binary tree
  //
  // Go through the questions the subject has actually visited in the questionnaire.
  //
  // Starting at the first question, we follow the
  // path forward: for a branching question (like a radio) we take the branch
  // the subject picked; for anything else we take the default "next" question.
  // We stop when we run out of questions or loop back to one we've already seen.
  //
  // Every question in the assignment is returned — walked or not — bundled
  // with its answers and an `isWalked` flag so the UI can highlight the path
  // the subject actually took vs. the questions they never reached.
  //
  const buildDependencyGraph = (questions: Question[], answers: Answer[], startId: string | null): ProjectedNode[] => {
    if (!startId) {
      throw new Error('Question startId is undefined')
    }

    // 1. Group answers per question — latest at index 0.
    const answersSorted = orderBy(answers, ['deviceTime', 'createdAt'], ['desc', 'desc'])
    const answersByQid = groupBy(answersSorted, 'questionId')
    const questionById = new Map(questions.map((q) => [q._id, q]))

    // Where does the walk go after `question`?
    // Radio + answer → the answered branch. Anything else → next[0].
    const nextIdAfter = (question: Question): string | null => {
      const latestAnswer = answersByQid[question._id]?.[0]
      if (isBranching(question.type) && latestAnswer) {
        const chosenIndex = parseInt(latestAnswer.value, 10)
        return question.next[chosenIndex] ?? question.next[0] ?? null
      }

      return question.next[0] ?? null
    }

    // 2. Walk from start until we hit a dead end or loop back.
    const walkedIds = new Set<string>()
    let currentId: string | null = startId
    while (currentId && !walkedIds.has(currentId)) {
      walkedIds.add(currentId)
      const question = questionById.get(currentId)
      if (!question) break
      currentId = nextIdAfter(question)
    }

    // 3. Tag every question with its answers + walked flag.
    return questions.map((q) => ({
      question: q,
      answers: answersByQid[q._id] ?? [],
      isWalked: walkedIds.has(q._id),
    }))
  }

  const projectedPath = useMemo(() => buildDependencyGraph(questions, answers, startId), [questions, answers, startId])
  //console.log('DependencyAssignmentView > projectedPath = ', projectedPath)

  useEffect(() => {
    logAssignmentDebug(data.assignment, lang, 'DependencyAssignmentView')
  }, [data.assignment, lang])

  return (
    <div className="container-fluid">
      <div style={{ width: '100%', height: '82vh', border: '1px solid #eee', borderRadius: 8 }}>
        <ReactFlowProvider>
          <FlowGraph questions={questions} projectedPath={projectedPath} lang={lang} startId={startId} endId={endId} />
        </ReactFlowProvider>
      </div>
    </div>
  )
}

type FlowGraphProps = {
  questions: Question[]
  lang: string
  startId: string | null
  endId: string | null
  projectedPath: ProjectedNode[]
}

const FlowGraph = ({ questions, lang, startId, endId, projectedPath }: FlowGraphProps) => {
  const [edges, setEdges] = useEdgesState<Edge>([])
  const [nodes, setNodes] = useState<any[]>([])

  useEffect(() => {
    const initialNodes = projectedPath.map((item, index) => ({
      id: item.question._id,
      type: 'questionNode',
      position: { x: 0, y: 0 },
      data: {
        question: item.question,
        defaultLang: lang,
        index,
        startQuestionId: startId,
        endQuestionId: endId,
        answers: item.answers,
        isWalked: item.isWalked,
      } satisfies QuestionNodeData,
    }))

    //
    // The edges is the lines between the nodes
    //
    const initialEdges = buildEdges(
      projectedPath.map((g) => g.question),
      lang
    )

    const { nodes: ln } = applyDagreLayout(initialNodes, initialEdges, 'TB')
    setNodes(ln)
    setEdges(initialEdges)
  }, [questions, lang, startId, endId, setEdges, projectedPath])

  //
  // enables the ability to drag nodes around
  const onNodesChange = (changes) => {
    if (changes.some((c: any) => c.type === 'remove')) return
    setNodes((snap) => applyNodeChanges(changes, snap))
  }

  const styledEdges = useMemo(() => {
    const WALKED_STYLE = { stroke: COLOR_PRIMARY, strokeWidth: 3 }
    const IDLE_STYLE = { stroke: '#D0D0D0', strokeWidth: 1.5 }

    // An edge is walked when both endpoints are on the walked path AND —
    // for branching sources — it's the edge on the answered option.
    const isEdgeWalked = (edge: Edge): boolean => {
      const source = projectedPath.find((g) => g.question._id === edge.source)
      const target = projectedPath.find((g) => g.question._id === edge.target)
      if (!source?.isWalked || !target?.isWalked) return false

      if (!isBranching(source.question.type)) return true

      const chosenIndex = source.answers[0] ? parseInt(source.answers[0].value, 10) : 0
      const edgeOptionIndex = parseInt(edge.id.split('-').pop() ?? '0', 10)
      return edgeOptionIndex === chosenIndex
    }

    return edges.map((e) => ({
      ...e,
      style: { ...e.style, ...(isEdgeWalked(e) ? WALKED_STYLE : IDLE_STYLE) },
    }))
  }, [edges, projectedPath])

  return (
    <ReactFlow
      nodes={nodes}
      edges={styledEdges}
      onNodesChange={onNodesChange}
      nodeTypes={nodeTypes}
      fitView={true}
      nodesDraggable={true}
      nodesConnectable={false}
      elementsSelectable={true}
      defaultEdgeOptions={{ type: 'smoothstep', style: { stroke: '#626262', strokeWidth: 1.5 } }}
    >
      <Controls showInteractive={false}></Controls>

      <Background variant={BackgroundVariant.Cross} gap={16} size={1} color="#A7A7A7" />
    </ReactFlow>
  )
}
